import { takeUntil, takeWhile } from 'rxjs/operators';

import { isServiceAuthOnly } from '@swf/core/models/casino/utils';
import { select } from '@swf/core/store';
import type { WidgetEffectParams } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';
import { currencyChange, login, logout } from '@swf/core/utils/actions';

import type { CasinoWidgetConfig } from '../types';

const empty = { games: [] };

export async function userUpdates(
  payload: undefined,
  {
    dispatch,
    actions,
    subscribe,
    rootState,
    widget
  }: WidgetEffectParams<Partial<CasinoWidgetConfig>>
) {
  const widgetDispatch = dispatch[widget.id];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

  if (!clientAuthenticated || !canUseDOM) return;

  if (!widget.config.hideOnLogin) {
    subscribe('/user/queue/casino/favorites')
      .pipe(takeUntil(onLogout))
      .subscribe(widgetDispatch.favorites);
    subscribe('/user/queue/fsb').pipe(takeUntil(onLogout)).subscribe(widgetDispatch.freeSpinBonus);
  }
}

export async function myRacesUpdates<T>(payload: undefined, params: WidgetEffectParams<T>) {
  const { actions, widget, dispatch, subscribe, rootState } = params;
  const widgetDispatch = dispatch[widget.id];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

  if (!clientAuthenticated || !canUseDOM) return;

  subscribe('/user/queue/races')
    .pipe(takeUntil(onLogout))
    .subscribe((data) => {
      widgetDispatch.raceStandingUpdate(data);
    });
}

export async function raceUpdate<T>(payload: string, params: WidgetEffectParams<T>) {
  const { widget, dispatch, subscribe, rootState } = params;
  const widgetDispatch = dispatch[widget.id];
  const state = rootState[widget.id];
  if (!canUseDOM) return;
  const path = `/topic/race.${payload}`;
  const reducer = () => !!state?.races[state.knownRaces[payload]];

  subscribe(path)
    .pipe(takeWhile(reducer))
    .subscribe((data) => widgetDispatch.applyRaceUpdate({ id: payload, body: data.body }));
}

export async function userLoggedIn(
  payload: undefined,
  params: WidgetEffectParams<
    Pick<CasinoWidgetConfig, 'params' | 'loggedInOnly' | 'hideOnLogin' | 'gameService'>
  >
) {
  const { widget, dispatch } = params;
  const widgetDispatch = dispatch[widget.id];

  await widgetDispatch.fetch();
}

export async function userLoggedOut(
  payload: undefined,
  params: WidgetEffectParams<
    Pick<CasinoWidgetConfig, 'params' | 'loggedInOnly' | 'hideOnLogin' | 'gameService'>
  >
) {
  const { widget, dispatch } = params;
  const widgetDispatch = dispatch[widget.id];
  const { gameService, loggedInOnly } = widget.config;

  if (loggedInOnly || isServiceAuthOnly(gameService)) widgetDispatch.set(empty);
}

export async function preload(
  payload: undefined,
  params: WidgetEffectParams<Pick<CasinoWidgetConfig, 'params' | 'loggedInOnly' | 'hideOnLogin'>>
) {
  const { widget, dispatch, actions, rootState } = params;
  const widgetDispatch = dispatch[widget.id];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const { loggedInOnly } = widget.config;

  (!loggedInOnly || clientAuthenticated) && (await widgetDispatch.fetch());

  currencyChange(actions)?.subscribe(({ state }) => {
    widgetDispatch.set(empty);
    if (!loggedInOnly || select.session.isClientAuthenticated(state)) {
      widgetDispatch.fetch();
    }
  });

  login(actions)?.subscribe(async () => {
    widgetDispatch.userLoggedIn?.();
    widgetDispatch.userUpdates();
  });

  logout(actions)?.subscribe(async () => {
    widgetDispatch.userLoggedOut && widgetDispatch.userLoggedOut();
  });

  if (clientAuthenticated && params) {
    userUpdates(undefined, params);
  }
}
